import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { GiftIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/solid';
import { useCart } from '../context/CartContext';
import { formatCurrency, getIndefiniteArticle } from '../utils/formatting';
import CampaignHeader from '../components/layout/CampaignHeader'; 

export default function CampaignProfilePage() {
  const { addToCart } = useCart();
  const { id } = useParams();
  const [quantity, setQuantity] = useState(1); // Quantity state for organization campaigns

  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaignType, setCampaignType] = useState('recipient');

  const brandColor = '#e46809';
  const brandColorHover = '#d15e08';

  useEffect(() => {
    const fetchCampaignData = async () => {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        setError("API URL is not configured.");
        setLoading(false);
        return;
      }
      
      try {
        if (id.startsWith('org-')) {
          setCampaignType('organization');
          const orgId = id.replace('org-', '');
          const response = await fetch(`${apiUrl}/org-campaigns/${orgId}`);
          
          if (!response.ok) {
            if (response.status === 404) throw new Error('Organization campaign not found.');
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          
          setCampaignData({
            id: `org-${data.id}`,
            type: 'organization',
            product_name: data.product_name,
            product_image_url: data.image_url,
            product_price: data.unit_price,
            organization_name: data.organization_name,
            first_name: data.first_name,
            last_name: data.last_name,
            reason: data.reason,
            amount_raised: data.amount_raised,
            units_fulfilled: data.units_fulfilled,
            units_remaining: data.units_remaining,
            total_price: data.total_price,
            quantity: data.quantity,
            specifications: data.specifications || {}
          });
        } else {
          setCampaignType('recipient');
          const response = await fetch(`${apiUrl}/campaigns/${id}`);
          
          if (!response.ok) {
            if (response.status === 404) throw new Error('Campaign not found.');
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          setCampaignData(data);
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchCampaignData();
  }, [id]);

  const handleAddToCart = () => {
  if (campaignType === 'organization') {
    // For organization campaigns, add with selected quantity
    const campaignWithQuantity = {
      ...campaignData,
      quantity: quantity, // Make sure this is included
      type: 'organization' // Make sure type is set
    };
    addToCart(campaignWithQuantity);
  } else {
    // For recipient campaigns, add as is (quantity will be set to 1 in the cart context)
    addToCart({
      ...campaignData,
      type: 'recipient' // Make sure type is set
    });
  }
};

  const increaseQuantity = () => {
    const maxAvailable = campaignData.units_remaining || campaignData.quantity;
    if (quantity < maxAvailable) {
      setQuantity(quantity + 1);
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  if (loading || error || !campaignData) {
    const title = loading ? "Loading..." : "Error";
    const message = loading ? "Loading Campaign Details..." : (error || 'Campaign could not be loaded.');
    return (
      <>
        <CampaignHeader title={title} />
        <div className="flex-grow flex items-center justify-center p-8">
          <p className={`text-2xl ${error ? 'text-red-500' : 'text-gray-500'}`}>{message}</p>
        </div>
      </>
    );
  }

// Update the displayName calculation to handle null organization_name
const displayName = campaignType === 'organization' 
  ? campaignData.organization_name || `${campaignData.first_name} ${campaignData.last_name}`.trim()
  : `${campaignData.first_name || ''} ${campaignData.last_name || ''}`.trim();
    
  const profileImageUrl = campaignData.profile_photo_url || `https://ui-avatars.com/api/?name=${displayName}&background=e46809&color=fff`;

  const productName = campaignData.product_name || 'specialized item';
  const article = getIndefiniteArticle(productName);

  return (
    <>
      <CampaignHeader title="Campaign Profile" />

      <div className="container mx-auto px-4 py-8 md:py-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            
            <div className="lg:col-span-2 flex justify-center">
              <div className="sticky top-28 w-full max-w-md">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
                  <img 
                    src={campaignData.product_image_url || 'https://via.placeholder.com/400'}
                    alt={campaignData.product_name || 'Campaign Product'} 
                    className="w-full h-auto object-contain aspect-square"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-white dark:bg-gray-800/50 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 md:p-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">{displayName}</h1>
                    <p className="text-lg text-gray-500 dark:text-gray-400 mt-1">
                      {campaignType === 'organization' ? 'Needs supplies: ' : 'Needs '}
                      {campaignType === 'organization' ? (
                        <span>
                          {campaignData.quantity} {productName}{campaignData.quantity > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span>{article} {productName}</span>
                      )}
                    </p>
                    {campaignType === 'organization' && campaignData.units_fulfilled > 0 && (
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        {campaignData.units_fulfilled} of {campaignData.quantity} already funded!
                      </p>
                    )}
                  </div>
                  
                  <img 
                    src={profileImageUrl}
                    alt={`Profile of ${displayName}`} 
                    className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-4 border-white dark:border-gray-700 flex-shrink-0 shadow-md"
                  />
                </div>
                
                <div className="border-t border-gray-200 dark:border-gray-700 my-6" />

                <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-base">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {campaignType === 'organization' ? 'Price per unit' : 'Price'}
                    </span>
                    <span className="font-bold text-gray-800 dark:text-gray-200 text-lg" style={{color: brandColor}}>
                      {formatCurrency(campaignData.product_price)}
                    </span>
                  </div>

                  {campaignType === 'organization' && (
                    <>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Needed</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {formatCurrency(campaignData.total_price)}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Units Funded</span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">
                          {campaignData.units_fulfilled || 0} / {campaignData.quantity}
                        </span>
                      </div>
                    </>
                  )}

                  {campaignData.employment_status && (
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Employment Status</span>
                      <span className="font-bold text-gray-800 dark:text-gray-200">{campaignData.employment_status}</span>
                    </div>
                  )}
                </div>

                {campaignType === 'organization' && campaignData.specifications && Object.keys(campaignData.specifications).length > 0 && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-6" />
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Specifications</h3>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(campaignData.specifications).map(([key, value]) => (
                          <div key={key} className="flex flex-col">
                            <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Quantity Selector for Organization Campaigns */}
                {campaignType === 'organization' && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-6" />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Quantity to Donate</span>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={decreaseQuantity}
                          disabled={quantity <= 1}
                          className="p-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <MinusIcon className="w-4 h-4" />
                        </button>
                        <span className="text-lg font-bold">{quantity}</span>
                        <button
                          onClick={increaseQuantity}
                          disabled={quantity >= (campaignData.units_remaining || campaignData.quantity)}
                          className="p-1 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <PlusIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      {quantity} unit{quantity !== 1 ? 's' : ''} × {formatCurrency(campaignData.product_price)} = {formatCurrency(campaignData.product_price * quantity)}
                    </div>
                  </>
                )}

                <div className="border-t border-gray-200 dark:border-gray-700 my-6" />

                <button 
                  onClick={handleAddToCart}
                  className="w-full flex items-center justify-center text-lg font-bold py-4 px-6 rounded-lg text-white transition-all duration-300 transform hover:scale-105" 
                  style={{ backgroundColor: brandColor }} 
                  onMouseOver={e => e.currentTarget.style.backgroundColor = brandColorHover} 
                  onMouseOut={e => e.currentTarget.style.backgroundColor = brandColor}
                >
                  <GiftIcon className="w-6 h-6 mr-3" />
                  {campaignType === 'organization' ? `Donate ${quantity} Unit${quantity !== 1 ? 's' : ''}` : 'Gift Now'}
                </button>
              </div>

              <div className="mt-10 space-y-8 text-gray-700 dark:text-gray-300 leading-relaxed text-lg">
                {campaignData.reason && (
                  <div>
                    <h3 className="font-bold text-xl mb-2 text-gray-900 dark:text-white">
                      {campaignType === 'organization' ? 'About This Campaign' : 'Reason for this Gift'}
                    </h3>
                    <p>{campaignData.reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}